import { Button } from "@/components/ui/button";
import type { RecorderErrorKind } from "@/lib/voice-order";
import { AlertTriangle, Mic, MicOff } from "lucide-react";
import type { ComponentType, SVGProps } from "react";

/** The per-error copy a flow supplies to {@link VoiceRecorderFailureCard}. */
export interface VoiceRecorderFailureCopy {
  title: string;
  body: string;
  canRetry: boolean;
}

interface VoiceRecorderFailureCardProps {
  error: RecorderErrorKind;
  copy: VoiceRecorderFailureCopy;
  onRetry: () => void;
  /** Label for the non-retry recovery action - "Search the catalog" for the
   *  order flow, "Cancel" for the message flow (which has no catalog to fall
   *  back to). */
  fallbackLabel: string;
  fallbackIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  onFallback: () => void;
}

/**
 * The failure card shared by every voice-capture flow (order, message): the
 * red icon (mic-off for a denied permission, a generic warning otherwise),
 * title/body, and the two recovery actions - retry (when `copy.canRetry`)
 * and a flow-specific fallback. Each flow keeps its own `ERROR_COPY` map and
 * fallback action (a catalog search vs. a plain cancel) - only this scaffold
 * is shared (KAN-192 review: the scaffold was byte-identical between
 * `VoiceRecorderPanel` and `VoiceMessageRecorderPanel`, only the copy and the
 * fallback action differed).
 */
export function VoiceRecorderFailureCard({
  error,
  copy,
  onRetry,
  fallbackLabel,
  fallbackIcon: FallbackIcon,
  onFallback
}: VoiceRecorderFailureCardProps) {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-8 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20">
        {error === "permission-denied" ? (
          <MicOff
            className="h-8 w-8 text-red-500 dark:text-red-400"
            aria-hidden="true"
          />
        ) : (
          <AlertTriangle
            className="h-8 w-8 text-red-500 dark:text-red-400"
            aria-hidden="true"
          />
        )}
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          {copy.title}
        </h3>
        <p className="mx-auto max-w-xs text-sm text-gray-500 dark:text-gray-400">
          {copy.body}
        </p>
      </div>
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
        {copy.canRetry ? (
          <Button type="button" onClick={onRetry}>
            <Mic className="mr-2 h-4 w-4" aria-hidden="true" />
            Try again
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onFallback}>
          {FallbackIcon ? (
            <FallbackIcon className="mr-2 h-4 w-4" aria-hidden="true" />
          ) : null}
          {fallbackLabel}
        </Button>
      </div>
    </div>
  );
}
